import React from "react";
import { useSelector } from "react-redux";

import { AnalyticsResourceType, MetricArrayType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

import { getDrawer } from "../../store/slices/global";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import { ExperienceFe } from "../../types/schemas/experiences";

import { GraphConfig } from "./Graphs/Utils/graphConfigs";
import { getPageBreakpoints } from "./Graphs/Utils/graphHelpers";
import { MetricDetailsDrawer } from "./Pages/AlwaysOnDashboard/MetricDetailsDrawer";
import { MetricDetailsTable } from "./Pages/AlwaysOnDashboard/MetricDetailsTable";
import { SingleGraphInnerWrapper } from "./SingleGraphInnerWrapper";

const PREFIX = "SingleGraphWrapper";

const classes = {
  dragIcon: `${PREFIX}-dragIcon`,
  graphPaper: `${PREFIX}-graphPaper`,
  fontFamily: `${PREFIX}-fontFamily`,
  loadingData: `${PREFIX}-loadingData`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.dragIcon}`]: {
    color: "rgba(0,0,0,0.38)",
    transform: "rotate(90deg)",
    "&:hover, &:focus": {
      color: theme.palette.primary.dark,
      transform: "rotate(90deg) scale(1.25)",
    },
  },

  [`& .${classes.graphPaper}`]: {
    padding: "30px",
  },

  [`& .${classes.fontFamily}`]: {
    fontFamily: theme.typography.fontFamily,
  },
  [`& .${classes.loadingData}`]: {
    marginTop: "150px",
    textAlign: "center",
  },
}));

export interface SingleGraphWrapperProps {
  resourceType: AnalyticsResourceType;
  resource?: ExperienceFe;
  data?:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  config: GraphConfig;
  totalTestVisitors?: number;
  graphMetricTotals?: MetricArrayType;
  metricFilterIndex?: number;
  rawDatasetData?: MetricArrayType;
}

export const SingleGraphWrapper = ({
  resource,
  data,
  config,
  totalTestVisitors,
  resourceType,
  graphMetricTotals,
  metricFilterIndex,
  rawDatasetData,
}: SingleGraphWrapperProps) => {
  const metricDetailsDrawer = useSelector(
    getDrawer("sitewideAnalyticsViewDetails")
  );

  return (
    <StyledGrid
      item
      sm={12}
      md={getPageBreakpoints("md", config, false)}
      lg={getPageBreakpoints("lg", config, false)}
    >
      {metricDetailsDrawer?.props?.config?.title === config.title && (
        <MetricDetailsDrawer
          graphComponent={
            <SingleGraphInnerWrapper
              isDrawerRender={true}
              resourceType={resourceType}
              config={config}
              data={data}
              resource={resource}
              totalTestVisitors={totalTestVisitors}
              graphMetricTotals={graphMetricTotals}
              metricFilterIndex={metricFilterIndex}
            />
          }
          tableComponent={
            rawDatasetData ? (
              <MetricDetailsTable
                data={data}
                config={config}
                graphMetricTotals={graphMetricTotals}
                rawDatasetData={rawDatasetData}
              />
            ) : null
          }
        />
      )}
      <SingleGraphInnerWrapper
        resourceType={resourceType}
        config={config}
        data={data}
        resource={resource}
        totalTestVisitors={totalTestVisitors}
        graphMetricTotals={graphMetricTotals}
        metricFilterIndex={metricFilterIndex}
      />
    </StyledGrid>
  );
};
